import { useMemo, useState } from "react";
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  BarChart3,
  Loader2,
  Minus,
} from "lucide-react";
import {
  validateChartData,
  sortBarChartData,
  isMultiSeries,
  E_BarSortMode,
  type T_BarSortMode,
} from "@lattice/frontend/types/charts";
import { BarChartComponent } from "./charts/BarChart";
import { LineChartComponent } from "./charts/LineChart";
import { PieChartComponent } from "./charts/PieChart";
import { cn } from "@lattice/frontend/lib/utils/ui";
import { useChatRenderContext } from "../lib/render-context";

const SORT_OPTIONS = [
  {
    mode: E_BarSortMode.DESC,
    label: "Highest",
    Icon: ArrowDownWideNarrow,
  },
  {
    mode: E_BarSortMode.ASC,
    label: "Lowest",
    Icon: ArrowUpWideNarrow,
  },
  {
    mode: E_BarSortMode.ORIGINAL,
    label: "Original",
    Icon: Minus,
  },
] as const;

export const ChartBlock = ({ code }: { code: string }) => {
  const validation = validateChartData(code);
  const { isStreaming } = useChatRenderContext();

  const defaultSortMode =
    validation.data?.type === "bar"
      ? E_BarSortMode.DESC
      : E_BarSortMode.ORIGINAL;

  const [sortMode, setSortMode] = useState<T_BarSortMode>(defaultSortMode);

  const displayData = useMemo(() => {
    if (!validation.data) return undefined;
    if (validation.data.type !== "bar") return validation.data;
    return sortBarChartData(validation.data, sortMode);
  }, [validation.data, sortMode]);

  if (!validation.success) {
    // While streaming, show a loading state — the chart JSON may still be incomplete
    if (isStreaming) {
      return (
        <div className="border border-amber-500/30 rounded-lg p-4 my-4 bg-amber-500/10">
          <div className="flex items-center gap-2 text-amber-100">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">
              Chart rendering in progress…
            </span>
          </div>
        </div>
      );
    }

    // Streaming is done but chart data is invalid — show error state
    return (
      <div className="border border-red-500/30 rounded-lg p-4 my-4 bg-red-500/10">
        <div className="flex items-center gap-2 text-red-200">
          <BarChart3 className="h-4 w-4" />
          <span className="text-sm font-medium">Chart failed to render</span>
        </div>
        <p className="text-xs text-red-200/80 mt-1">
          {validation.error ?? "The chart data could not be parsed."}
        </p>
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-red-300/60 hover:text-red-200">
            View raw chart data
          </summary>
          <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-48">
            {code}
          </pre>
        </details>
      </div>
    );
  }

  const chartData = displayData;

  if (!chartData) return null;

  return (
    <div className="chart-container border border-grey-7-border rounded-lg p-4 my-4 bg-grey-7">
      {chartData.title ? (
        <div className="flex items-center gap-4 mb-4">
          <BarChart3 className="text-primary shrink-0" size={24} />
          <h4 className="md:text-lg font-semibold flex-1">{chartData.title}</h4>
          {chartData.type === "bar" && (
            <div className="bg-grey-7 rounded-lg p-1 border border-grey-7-border inline-flex">
              {SORT_OPTIONS.map(({ mode, label, Icon }) => (
                <button
                  key={mode}
                  className={cn(
                    "rounded-md px-2 py-1 text-xs font-medium cursor-pointer transition-all flex items-center gap-1",
                    mode === sortMode
                      ? "bg-[#D4D4D4] text-black shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/5",
                  )}
                  onClick={() => setSortMode(mode)}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        chartData.type === "bar" && (
          <div className="flex justify-end mb-4">
            <div className="bg-grey-7 rounded-lg p-1 border border-grey-7-border inline-flex">
              {SORT_OPTIONS.map(({ mode, label, Icon }) => (
                <button
                  key={mode}
                  className={cn(
                    "rounded-md px-2 py-1 text-xs font-medium cursor-pointer transition-all flex items-center gap-1",
                    mode === sortMode
                      ? "bg-[#D4D4D4] text-black shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/5",
                  )}
                  onClick={() => setSortMode(mode)}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        )
      )}

      {chartData.type === "bar" && <BarChartComponent data={chartData} />}
      {chartData.type === "line" && <LineChartComponent data={chartData} />}
      {chartData.type === "pie" && <PieChartComponent data={chartData} />}
      {chartData.type === "area" && (
        <LineChartComponent data={chartData} area />
      )}

      <details className="mt-4 text-sm">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          View data table
        </summary>
        {isMultiSeries(chartData) && chartData.series ? (
          // Multi-series table with columns for each series
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Label</th>
                {chartData.series.map((seriesName) => (
                  <th key={seriesName} className="text-right p-2">
                    {seriesName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.data.map((row, i) => {
                const rowRecord: Record<string, unknown> = row;
                return (
                  <tr key={i} className="border-b">
                    <td className="p-2">{row.label}</td>
                    {chartData.series?.map((seriesName) => {
                      const rawVal = rowRecord[seriesName];
                      const val =
                        typeof rawVal === "number" ||
                        typeof rawVal === "string" ||
                        rawVal === null
                          ? rawVal
                          : null;
                      return (
                        <td key={seriesName} className="text-right p-2">
                          {val === null
                            ? "—"
                            : typeof val === "number"
                              ? val.toLocaleString()
                              : val}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          // Single-series table (backward compatible)
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Label</th>
                <th className="text-right p-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {chartData.data.map((row, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{row.label}</td>
                  <td className="text-right p-2">
                    {"value" in row && typeof row.value === "number"
                      ? row.value.toLocaleString()
                      : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </div>
  );
};
